import { debounce } from 'lodash-es'
import { getStars } from '~/api'
import type { Star } from '~/api/interface'

/**
 *
 * @param list 模板列表，模板需要有query_star字段，用来标识
 * 是否已经查询过is_personal_star
 * @params star_model_type 2: 角色 3: youyan_config内数据
 * @returns
 */
export function useStarPatchList<
  T extends { id: number; query_star?: boolean; is_star_personal?: boolean },
>(list: ComputedRef<T[]>, star_model_type: 2 | 3) {
  const calList = ref<T[]>([]) as Ref<T[]>
  // 已经获取过is_star_personal的数据
  let startFetched: Star[] = []
  const session = useSession()
  const status = ref<'waiting' | 'success' | 'error'>('waiting')

  // is_personal_star只在客户端才会查询，因此有些模板没有带star数据，这时候需要在客户端查询
  watch(
    [() => list.value],
    async ([_list]) => {
      status.value = 'waiting'
      calList.value = _list
      if (import.meta.client) {
        // is_personal_star没有查询的时候
        const hasNoStarFetched = _list
          .filter((item) => {
            if (item.query_star) {
              return false
            }

            const fetchedStar = startFetched.find(
              (template) => item.id === template.id
            )

            if (fetchedStar) {
              return false
            }

            return true
          })
          .map((item) => item.id)

        if (hasNoStarFetched.length > 0 && session.value?.session_id) {
          try {
            const stars = await getStars({
              ids: hasNoStarFetched.join(','),
              star_model_type,
            })

            startFetched = startFetched.concat(stars.data || [])
          } catch (error) {
            status.value = 'error'
          }
        }

        calList.value.forEach((item) => {
          const target = startFetched.find((star) => star.id === item.id)

          if (target) {
            item.is_star_personal = target.is_star || item.is_star_personal
          }
        })
      }
      status.value = 'success'
    },
    { immediate: true, flush: 'sync' }
  )

  return { calList, status }
}

/**
 *
 * @param data
 * @param star_model_type  2: 角色 3: youyan_config内数据
 * @returns
 */
export function useStartPatch<
  T extends { id: number; query_star?: boolean; is_star_personal?: boolean },
>(data: Ref<T | null | undefined>, star_model_type: 2 | 3) {
  const calData = ref<T | undefined | null>()
  const session = useSession()
  const status = ref<'waiting' | 'success' | 'error'>('waiting')

  watch(
    data,
    async (_template) => {
      calData.value = _template
      status.value = 'waiting'
      if (
        _template &&
        import.meta.client &&
        !_template.query_star &&
        session.value?.session_id
      ) {
        try {
          const stars = await getStars({
            ids: `${_template.id}`,
            star_model_type,
          })

          const is_star_personal =
            stars.data?.[0].is_star || _template.is_star_personal
          // 更新star
          calData.value!.is_star_personal = is_star_personal
        } catch (error) {
          status.value = 'error'
        }
      }
      status.value = 'success'
    },
    { immediate: true, flush: 'sync' }
  )

  return { calData, status }
}
